'use client';

import type { IChallengeClip } from '@repo/sdk';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@repo/ui';
import { useRouter } from 'next/navigation';
import type { JSX } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { EmptyState } from '../empty-state';
import { CropBatchBar } from './crop-batch-bar';
import { CropTableRow } from './crop-table-row';
import { CropVideoPreview } from './crop-video-preview';
import { useCheckClipVideos } from './use-check-clip-videos';
import { useCropAction } from './use-crop-action';

interface ICropDashboardProps {
  clips: IChallengeClip[];
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: single-responsibility component
export function CropDashboard({ clips }: ICropDashboardProps): JSX.Element {
  const router = useRouter();
  const { withVideo, isLoading } = useCheckClipVideos(clips);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [previewClip, setPreviewClip] = useState<IChallengeClip | null>(null);
  const { isCropping, cropClip } = useCropAction();

  const pendingSelected = useMemo(
    () =>
      withVideo.filter(
        (c) => selected.has(c.id) && (c.cropStatus === 'pending' || c.cropStatus === 'failed'),
      ).length,
    [withVideo, selected],
  );

  const handleToggle = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleCrop = useCallback(
    async (clip: IChallengeClip) => {
      if (!clip.startMs || !clip.endMs || !clip.vaultPath) {
        return;
      }
      const filename = `clip-${clip.id}.mp4`;
      const ok = await cropClip(clip.id, clip.vaultPath, clip.startMs, clip.endMs, filename);
      if (ok) {
        router.refresh();
      }
    },
    [cropClip, router],
  );

  const handleBatchCrop = useCallback(async () => {
    for (const clip of withVideo.filter(
      (c) => selected.has(c.id) && (c.cropStatus === 'pending' || c.cropStatus === 'failed'),
    )) {
      await handleCrop(clip);
    }
  }, [withVideo, selected, handleCrop]);

  if (clips.length === 0) {
    return (
      <EmptyState title="No clips" description="Generate clips from the Generate tab first." />
    );
  }
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">Checking video files...</p>
    );
  }
  if (withVideo.length === 0) {
    return (
      <EmptyState
        title="No video files"
        description={`${clips.length} clips found but none have video files in the vault.`}
      />
    );
  }

  return (
    <div className="space-y-4">
      <CropBatchBar
        selectedCount={selected.size}
        pendingSelected={pendingSelected}
        isCropping={isCropping}
        onCropSelected={handleBatchCrop}
        onSelectAll={() => setSelected(new Set(withVideo.map((c) => c.id)))}
        onDeselectAll={() => setSelected(new Set())}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Challenge</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Timing</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Error</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {withVideo.map((clip) => (
            <CropTableRow
              key={clip.id}
              clip={clip}
              isSelected={selected.has(clip.id)}
              isCropping={isCropping}
              onToggle={handleToggle}
              onCrop={handleCrop}
              onPreview={setPreviewClip}
            />
          ))}
        </TableBody>
      </Table>
      {previewClip?.baseLocalPath && (
        <CropVideoPreview
          localPath={previewClip.baseLocalPath}
          onClose={() => setPreviewClip(null)}
        />
      )}
    </div>
  );
}
